import {
  Button,
  Modal,
  SpaceBetween,
  Spinner,
} from "@cloudscape-design/components";
import { FC, useEffect, useState } from "react";
import LoadingBar from "@cloudscape-design/chat-components/loading-bar";
import { useAuthContext } from "../../contexts/AuthContext";
import { GraphLink } from "../../models/GraphLink";
import {
  createItem,
  deleteItem,
  fetchItemsByIds,
  fetchItemsWithOrFilters,
} from "../../api/generic";
import { prefixCollection } from "../../utils/prefix";
import AlgoliaSearch, {
  AlgoliaRecord,
  typeToCollection,
} from "./AlgoliaSearch";
import { capitalizeFirstLetter } from "../../utils/string";

interface GridLinkModalProps {
  showModal: boolean;
  setShowModal: (val: boolean) => void;
  fromId: string;
  fromType: string;
  toTypes: string[];
  onChange?: () => void;
}

const GridLinkModal: FC<GridLinkModalProps> = ({
  showModal,
  setShowModal,
  fromId,
  fromType,
  toTypes,
  onChange,
}) => {
  const { tenant, userObject } = useAuthContext();
  const [isLoading, setIsLoading] = useState(true);
  const [graphData, setGraphData] = useState<GraphLink[]>([]);
  const [objectData, setObjectData] = useState<{ id: string; label: string }[]>(
    []
  );

  const fetchGraphData = async () => {
    const graph = await fetchItemsWithOrFilters<GraphLink>(
      prefixCollection("graphLinks", tenant),
      {
        or: [
          { field: "from", operator: "==", value: fromId },
          { field: "to", operator: "==", value: fromId },
        ],
      }
    );
    setGraphData(graph);

    const labelFetcher = async () => {
      const allLabels: { id: string; label: string }[] = [];

      for (const type of toTypes) {
        const relevantIds = graph
          .filter((x) => x.type.includes(type))
          .map((x) => (x.from === fromId ? x.to : x.from));

        if (relevantIds.length === 0) continue;

        const objects = await fetchItemsByIds<any>(
          prefixCollection(typeToCollection[type] || `${type}s`, tenant),
          relevantIds
        );

        const labels = objects.map((x: any) => ({
          id: x.id,
          label: x.name || x.title || x.batchId || x.supplierName || x.id,
        }));

        allLabels.push(...labels);
      }

      return allLabels;
    };

    setObjectData(await labelFetcher());
    setIsLoading(false);
  };

  const handleDeleteLink = async (linkId?: string) => {
    if (!linkId) return;
    setIsLoading(true);
    await deleteItem(prefixCollection("graphLinks", tenant), linkId);
    await fetchGraphData();
    if (onChange) onChange();
    setIsLoading(false);
  };

  const handleCreateLink = async (linkto: AlgoliaRecord) => {
    setIsLoading(true);
    const itemToAdd: GraphLink = {
      from: fromId,
      to: linkto.itemId,
      type: [fromType, linkto.type],
      createdAt: new Date(),
      linkedBy: userObject?.id ?? userObject?.uid ?? "",
    };

    await createItem<GraphLink>(
      prefixCollection("graphLinks", tenant),
      itemToAdd
    );
    await fetchGraphData();
    if (onChange) onChange();
    setIsLoading(false);
  };

  useEffect(() => {
    if (showModal) {
      fetchGraphData();
    }
  }, [showModal]);

  return (
    <Modal
      visible={showModal}
      onDismiss={() => setShowModal(false)}
      header={`Link to ${toTypes.join(", ")}`}
      footer={
        <>
          <div style={{ height: 10 }}>
            {isLoading && <LoadingBar variant="gen-ai" />}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div />
            <Button onClick={() => setShowModal(false)}>Close</Button>
          </div>
        </>
      }
    >
      <SpaceBetween size="m" direction="vertical">
        <AlgoliaSearch
          onSelectRecord={handleCreateLink}
          placeholder={`Search ${toTypes.join(", ")}`}
          tenantId={tenant}
          types={toTypes}
        />
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {graphData.map((link) => {
            const linkId = link.from === fromId ? link.to : link.from;
            const data = objectData.find((x) => x.id === linkId);
            return (
              <Button
                key={link.id}
                iconName="close"
                iconAlign="right"
                onClick={() => handleDeleteLink(link.id)}
              >
                {data?.label ?? (isLoading ? <Spinner /> : linkId)}{" "}
                <small>
                  {capitalizeFirstLetter(
                    link.from === fromId ? link.type[1] : link.type[0]
                  )}
                </small>
              </Button>
            );
          })}
        </div>
      </SpaceBetween>
    </Modal>
  );
};

export default GridLinkModal;
